import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Car, Plus, Trash2, Fuel, Wrench, Bell, BarChart3, FileText,
  Calendar, MapPin, ArrowRight, CheckCircle2, AlertCircle, Edit,
  TrendingUp, Clock, Settings2, ChevronDown, ChevronUp, X
} from "lucide-react";
import { useOrgCurrency } from "@/lib/currency-context";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtKm = (n) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(n || 0);

/* ========== FAHRZEUGSTUDIO HAUPTKOMPONENTE ========== */
export const FahrzeugStudio = () => {
  const { currency, formatMoney: fmt } = useOrgCurrency();
  const [activeTab, setActiveTab] = useState("fahrzeuge");
  const [vehicles, setVehicles] = useState([]);
  const [fahrten, setFahrten] = useState([]);
  const [tankbuch, setTankbuch] = useState([]);
  const [wartungen, setWartungen] = useState([]);
  const [kosten, setKosten] = useState([]);
  const [erinnerungen, setErinnerungen] = useState([]);
  const [statistiken, setStatistiken] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(false);

  const getOrgId = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user.org_id || user.organization_id || "default";
    } catch { return "default"; }
  };

  const loadAll = useCallback(async () => {
    const orgId = getOrgId();
    const vFilter = selectedVehicle ? `&vehicle_id=${selectedVehicle}` : "";
    try {
      const [vRes, fRes, tRes, wRes, kRes, eRes, sRes] = await Promise.all([
        axios.get(`${API}/reise/vehicles?org_id=${orgId}`),
        axios.get(`${API}/reise/vehicles/fahrten?org_id=${orgId}${vFilter}`),
        axios.get(`${API}/reise/vehicles/tankbuch?org_id=${orgId}${vFilter}`),
        axios.get(`${API}/reise/vehicles/wartungen?org_id=${orgId}${vFilter}`),
        axios.get(`${API}/reise/vehicles/kosten?org_id=${orgId}${vFilter}`),
        axios.get(`${API}/reise/vehicles/erinnerungen?org_id=${orgId}${vFilter}`),
        axios.get(`${API}/reise/vehicles/statistiken?org_id=${orgId}${vFilter ? `&vehicle_id=${selectedVehicle}` : ""}`),
      ]);
      setVehicles(vRes.data.vehicles || []);
      setFahrten(fRes.data.fahrten || []);
      setTankbuch(tRes.data.tankbuch || []);
      setWartungen(wRes.data.wartungen || []);
      setKosten(kRes.data.kosten || []);
      setErinnerungen(eRes.data.erinnerungen || []);
      setStatistiken(sRes.data.statistiken || []);
    } catch (err) {
      console.error("Fahrzeugstudio Laden fehlgeschlagen:", err);
    }
  }, [selectedVehicle]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const tabs = [
    { id: "fahrzeuge", label: "Fahrzeuge", icon: Car },
    { id: "fahrtenbuch", label: "Fahrtenbuch", icon: MapPin },
    { id: "tankbuch", label: "Tankbuch", icon: Fuel },
    { id: "wartung", label: "Wartung", icon: Wrench },
    { id: "kosten", label: "Kosten", icon: TrendingUp },
    { id: "erinnerungen", label: "Erinnerungen", icon: Bell },
    { id: "auswertung", label: "Auswertung", icon: BarChart3 },
  ];

  const offeneErinnerungen = erinnerungen.filter(e => !e.erledigt).length;

  return (
    <Card className="p-5 border-[var(--brand-border)] mt-4" data-testid="fahrzeug-studio">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-semibold text-[var(--brand-ink)] flex items-center gap-2">
          <Car className="w-5 h-5" /> Fahrzeugstudio
        </h3>
        {vehicles.length > 0 && (
          <select
            className="h-9 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm"
            value={selectedVehicle || ""}
            onChange={(e) => setSelectedVehicle(e.target.value || null)}
          >
            <option value="">Alle Fahrzeuge</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>{v.kennzeichen} – {v.marke} {v.modell}</option>
            ))}
          </select>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-[var(--brand-border-light)] pb-2">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                activeTab === tab.id
                  ? "bg-[var(--brand-primary)] text-white"
                  : "text-[var(--brand-muted)] hover:bg-[var(--brand-bg)]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
              {tab.id === "erinnerungen" && offeneErinnerungen > 0 && (
                <span className="ml-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{offeneErinnerungen}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "fahrzeuge" && <FahrzeugVerwaltung vehicles={vehicles} onReload={loadAll} getOrgId={getOrgId} />}
      {activeTab === "fahrtenbuch" && <Fahrtenbuch fahrten={fahrten} vehicles={vehicles} onReload={loadAll} getOrgId={getOrgId} />}
      {activeTab === "tankbuch" && <TankbuchTab tankbuch={tankbuch} vehicles={vehicles} onReload={loadAll} getOrgId={getOrgId} />}
      {activeTab === "wartung" && <WartungsManager wartungen={wartungen} vehicles={vehicles} onReload={loadAll} getOrgId={getOrgId} />}
      {activeTab === "kosten" && <KostenUebersicht kosten={kosten} vehicles={vehicles} statistiken={statistiken} onReload={loadAll} getOrgId={getOrgId} />}
      {activeTab === "erinnerungen" && <ErinnerungenTab erinnerungen={erinnerungen} vehicles={vehicles} onReload={loadAll} getOrgId={getOrgId} />}
      {activeTab === "auswertung" && <Auswertungen statistiken={statistiken} vehicles={vehicles} fahrten={fahrten} />}
    </Card>
  );
};

/* ========== 1. FAHRZEUGVERWALTUNG ========== */
const FahrzeugVerwaltung = ({ vehicles, onReload, getOrgId }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ kennzeichen: "", marke: "", modell: "", baujahr: "", typ: "eigentum", kraftstoff: "benzin", notizen: "" });

  const handleSave = async () => {
    if (!form.kennzeichen || !form.marke || !form.modell) {
      toast.error("Bitte Kennzeichen, Marke und Modell angeben");
      return;
    }
    try {
      await axios.post(`${API}/reise/vehicles?org_id=${getOrgId()}`, {
        ...form,
        baujahr: form.baujahr ? parseInt(form.baujahr) : null
      });
      toast.success("Fahrzeug angelegt");
      setForm({ kennzeichen: "", marke: "", modell: "", baujahr: "", typ: "eigentum", kraftstoff: "benzin", notizen: "" });
      setShowForm(false);
      onReload();
    } catch (err) {
      toast.error("Fehler beim Anlegen");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Fahrzeug wirklich löschen? Alle zugehörigen Daten bleiben erhalten.")) return;
    try {
      await axios.delete(`${API}/reise/vehicles/${id}?org_id=${getOrgId()}`);
      toast.success("Fahrzeug gelöscht");
      onReload();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--brand-muted)]">{vehicles.length} Fahrzeug(e) registriert</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> Fahrzeug hinzufügen
        </Button>
      </div>

      {showForm && (
        <Card className="p-4 border-[var(--brand-border)] bg-[var(--brand-bg)]">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Kennzeichen *</Label><Input placeholder="z.B. M-AB 1234" value={form.kennzeichen} onChange={e => setForm({...form, kennzeichen: e.target.value})} /></div>
            <div><Label className="text-xs">Marke *</Label><Input placeholder="z.B. BMW" value={form.marke} onChange={e => setForm({...form, marke: e.target.value})} /></div>
            <div><Label className="text-xs">Modell *</Label><Input placeholder="z.B. 320d" value={form.modell} onChange={e => setForm({...form, modell: e.target.value})} /></div>
            <div><Label className="text-xs">Baujahr</Label><Input type="number" placeholder="2023" value={form.baujahr} onChange={e => setForm({...form, baujahr: e.target.value})} /></div>
            <div>
              <Label className="text-xs">Eigentum/Leasing</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.typ} onChange={e => setForm({...form, typ: e.target.value})}>
                <option value="eigentum">Eigentum</option>
                <option value="leasing">Leasing</option>
                <option value="finanzierung">Finanzierung</option>
                <option value="dienstwagen">Dienstwagen</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Kraftstoff</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.kraftstoff} onChange={e => setForm({...form, kraftstoff: e.target.value})}>
                <option value="benzin">Benzin</option>
                <option value="diesel">Diesel</option>
                <option value="elektro">Elektro</option>
                <option value="hybrid">Hybrid (Plug-in)</option>
                <option value="gas">Gas (LPG/CNG)</option>
              </select>
            </div>
          </div>
          <div className="mt-3"><Label className="text-xs">Notizen</Label><Textarea placeholder="Optionale Notizen..." value={form.notizen} onChange={e => setForm({...form, notizen: e.target.value})} rows={2} /></div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleSave} className="bg-[var(--brand-ink)] text-white">Speichern</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      {vehicles.length === 0 ? (
        <div className="text-center py-8 text-[var(--brand-muted)]">
          <Car className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Noch keine Fahrzeuge. Lege dein erstes Fahrzeug an.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {vehicles.map(v => (
            <div key={v.id} className="flex items-center justify-between rounded-lg border border-[var(--brand-border)] px-4 py-3 hover:bg-[var(--brand-bg)] transition">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--brand-primary)]/10 flex items-center justify-center">
                  <Car className="w-5 h-5 text-[var(--brand-primary)]" />
                </div>
                <div>
                  <div className="font-medium text-sm text-[var(--brand-ink)]">{v.kennzeichen}</div>
                  <div className="text-xs text-[var(--brand-muted)]">{v.marke} {v.modell} {v.baujahr ? `(${v.baujahr})` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  v.typ === "eigentum" ? "bg-emerald-100 text-emerald-700" :
                  v.typ === "leasing" ? "bg-blue-100 text-blue-700" :
                  v.typ === "finanzierung" ? "bg-amber-100 text-amber-700" :
                  "bg-purple-100 text-purple-700"
                }`}>{v.typ}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{v.kraftstoff}</span>
                <Button size="icon" variant="ghost" className="text-red-500 h-7 w-7" onClick={() => handleDelete(v.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========== 2. FAHRTENBUCH ========== */
const Fahrtenbuch = ({ fahrten, vehicles, onReload, getOrgId }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "", datum: new Date().toISOString().split("T")[0],
    start_ort: "", ziel_ort: "", km_start: "", km_ende: "",
    fahrtzweck: "geschaeftlich", zweck_beschreibung: "", kunde_projekt: ""
  });

  useEffect(() => {
    if (vehicles.length > 0 && !form.vehicle_id) {
      setForm(f => ({ ...f, vehicle_id: vehicles[0].id }));
    }
  }, [vehicles]);

  const handleSave = async () => {
    if (!form.vehicle_id || !form.start_ort || !form.ziel_ort || !form.km_start || !form.km_ende) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    if (parseFloat(form.km_ende) <= parseFloat(form.km_start)) {
      toast.error("km-Ende muss größer als km-Start sein");
      return;
    }
    try {
      await axios.post(`${API}/reise/vehicles/fahrten?org_id=${getOrgId()}`, {
        ...form,
        km_start: parseFloat(form.km_start),
        km_ende: parseFloat(form.km_ende)
      });
      toast.success("Fahrt erfasst");
      setForm({ vehicle_id: vehicles[0]?.id || "", datum: new Date().toISOString().split("T")[0], start_ort: "", ziel_ort: "", km_start: "", km_ende: "", fahrtzweck: "geschaeftlich", zweck_beschreibung: "", kunde_projekt: "" });
      setShowForm(false);
      onReload();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Fahrt löschen?")) return;
    try {
      await axios.delete(`${API}/reise/vehicles/fahrten/${id}?org_id=${getOrgId()}`);
      toast.success("Gelöscht");
      onReload();
    } catch { toast.error("Fehler"); }
  };

  const getVehicleLabel = (vid) => {
    const v = vehicles.find(x => x.id === vid);
    return v ? `${v.kennzeichen}` : "–";
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--brand-muted)]">{fahrten.length} Fahrt(en) erfasst</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> Neue Fahrt
        </Button>
      </div>

      {showForm && (
        <Card className="p-4 border-[var(--brand-border)] bg-[var(--brand-bg)]">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Fahrzeug *</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.vehicle_id} onChange={e => setForm({...form, vehicle_id: e.target.value})}>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.kennzeichen} – {v.marke} {v.modell}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Datum *</Label><Input type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} /></div>
            <div>
              <Label className="text-xs">Fahrtzweck *</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.fahrtzweck} onChange={e => setForm({...form, fahrtzweck: e.target.value})}>
                <option value="geschaeftlich">Geschäftlich</option>
                <option value="privat">Privat</option>
              </select>
            </div>
            <div><Label className="text-xs">Startort *</Label><Input placeholder="z.B. München, Büro" value={form.start_ort} onChange={e => setForm({...form, start_ort: e.target.value})} /></div>
            <div><Label className="text-xs">Zielort *</Label><Input placeholder="z.B. Nürnberg, Kunde XY" value={form.ziel_ort} onChange={e => setForm({...form, ziel_ort: e.target.value})} /></div>
            <div><Label className="text-xs">Kunde/Projekt</Label><Input placeholder="Optional" value={form.kunde_projekt} onChange={e => setForm({...form, kunde_projekt: e.target.value})} /></div>
            <div><Label className="text-xs">km-Stand Start *</Label><Input type="number" step="0.1" placeholder="z.B. 45230" value={form.km_start} onChange={e => setForm({...form, km_start: e.target.value})} /></div>
            <div><Label className="text-xs">km-Stand Ende *</Label><Input type="number" step="0.1" placeholder="z.B. 45410" value={form.km_ende} onChange={e => setForm({...form, km_ende: e.target.value})} /></div>
            <div><Label className="text-xs">Beschreibung</Label><Input placeholder="Zweck der Fahrt" value={form.zweck_beschreibung} onChange={e => setForm({...form, zweck_beschreibung: e.target.value})} /></div>
          </div>
          {form.km_start && form.km_ende && parseFloat(form.km_ende) > parseFloat(form.km_start) && (
            <div className="mt-2 text-xs text-[var(--brand-muted)]">
              Gefahren: <strong>{fmtKm(parseFloat(form.km_ende) - parseFloat(form.km_start))} km</strong>
              {form.fahrtzweck === "geschaeftlich" && <> · Pauschale: <strong>{fmt((parseFloat(form.km_ende) - parseFloat(form.km_start)) * 0.30)}</strong></>}
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleSave} className="bg-[var(--brand-ink)] text-white">Speichern</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      {fahrten.length === 0 ? (
        <div className="text-center py-6 text-[var(--brand-muted)]">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Noch keine Fahrten erfasst.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {fahrten.slice(0, 20).map(f => (
            <div key={f.id} className="flex items-center justify-between rounded-lg border border-[var(--brand-border-light)] px-3 py-2 text-sm">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className={`w-2 h-2 rounded-full shrink-0 ${f.fahrtzweck === "geschaeftlich" ? "bg-emerald-500" : "bg-gray-400"}`} />
                <span className="text-xs text-[var(--brand-muted)] w-20 shrink-0">{f.datum}</span>
                <span className="truncate text-[var(--brand-ink)]">{f.start_ort} → {f.ziel_ort}</span>
                <span className="text-xs text-[var(--brand-muted)] shrink-0 ml-1">[{getVehicleLabel(f.vehicle_id)}]</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-medium text-xs">{fmtKm(f.km_gefahren)} km</span>
                {f.fahrtzweck === "geschaeftlich" && <span className="text-[10px] text-emerald-600">{fmt(f.km_gefahren * 0.30)}</span>}
                <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400" onClick={() => handleDelete(f.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
          {fahrten.length > 20 && <p className="text-xs text-center text-[var(--brand-muted)]">... und {fahrten.length - 20} weitere</p>}
        </div>
      )}
    </div>
  );
};

/* ========== 3. TANKBUCH ========== */
const TankbuchTab = ({ tankbuch, vehicles, onReload, getOrgId }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "", datum: new Date().toISOString().split("T")[0],
    tankstelle: "", liter: "", preis_gesamt: "", km_stand: "", kraftstoff: "benzin"
  });

  useEffect(() => {
    if (vehicles.length > 0 && !form.vehicle_id) {
      setForm(f => ({ ...f, vehicle_id: vehicles[0].id }));
    }
  }, [vehicles]);

  const handleSave = async () => {
    if (!form.vehicle_id || !form.liter || !form.preis_gesamt || !form.km_stand) {
      toast.error("Bitte Liter, Preis und km-Stand angeben");
      return;
    }
    try {
      await axios.post(`${API}/reise/vehicles/tankbuch?org_id=${getOrgId()}`, {
        ...form,
        liter: parseFloat(form.liter),
        preis_gesamt: parseFloat(form.preis_gesamt),
        km_stand: parseFloat(form.km_stand)
      });
      toast.success("Tankeintrag gespeichert");
      setForm({ vehicle_id: vehicles[0]?.id || "", datum: new Date().toISOString().split("T")[0], tankstelle: "", liter: "", preis_gesamt: "", km_stand: "", kraftstoff: "benzin" });
      setShowForm(false);
      onReload();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tankeintrag löschen?")) return;
    try {
      await axios.delete(`${API}/reise/vehicles/tankbuch/${id}?org_id=${getOrgId()}`);
      toast.success("Gelöscht");
      onReload();
    } catch { toast.error("Fehler"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--brand-muted)]">{tankbuch.length} Tankeinträge</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> Neuer Tankeintrag
        </Button>
      </div>

      {showForm && (
        <Card className="p-4 border-[var(--brand-border)] bg-[var(--brand-bg)]">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Fahrzeug *</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.vehicle_id} onChange={e => setForm({...form, vehicle_id: e.target.value})}>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.kennzeichen} – {v.marke} {v.modell}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Datum *</Label><Input type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} /></div>
            <div><Label className="text-xs">Tankstelle</Label><Input placeholder="z.B. Shell Hauptstr." value={form.tankstelle} onChange={e => setForm({...form, tankstelle: e.target.value})} /></div>
            <div><Label className="text-xs">Liter *</Label><Input type="number" step="0.01" placeholder="z.B. 45.5" value={form.liter} onChange={e => setForm({...form, liter: e.target.value})} /></div>
            <div><Label className="text-xs">Gesamtpreis ({currency}) *</Label><Input type="number" step="0.01" placeholder="z.B. 82.50" value={form.preis_gesamt} onChange={e => setForm({...form, preis_gesamt: e.target.value})} /></div>
            <div><Label className="text-xs">km-Stand *</Label><Input type="number" step="0.1" placeholder="z.B. 45600" value={form.km_stand} onChange={e => setForm({...form, km_stand: e.target.value})} /></div>
          </div>
          {form.liter && form.preis_gesamt && (
            <div className="mt-2 text-xs text-[var(--brand-muted)]">
              Preis/Liter: <strong>{fmt(parseFloat(form.preis_gesamt) / parseFloat(form.liter))}</strong>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleSave} className="bg-[var(--brand-ink)] text-white">Speichern</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      {tankbuch.length === 0 ? (
        <div className="text-center py-6 text-[var(--brand-muted)]">
          <Fuel className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Noch keine Tankeinträge.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {tankbuch.slice(0, 20).map(t => (
            <div key={t.id} className="flex items-center justify-between rounded-lg border border-[var(--brand-border-light)] px-3 py-2 text-sm">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="text-xs text-[var(--brand-muted)] w-20 shrink-0">{t.datum}</span>
                <span className="truncate text-[var(--brand-ink)]">{t.tankstelle || "–"}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs">{t.liter} L</span>
                <span className="font-medium text-xs">{fmt(t.preis_gesamt)}</span>
                <span className="text-[10px] text-[var(--brand-muted)]">{fmt(t.preis_pro_liter)}/L</span>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400" onClick={() => handleDelete(t.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========== 4. WARTUNGSMANAGER ========== */
const WartungsManager = ({ wartungen, vehicles, onReload, getOrgId }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "", datum: new Date().toISOString().split("T")[0],
    typ: "inspektion", beschreibung: "", kosten: "", naechster_termin: "", werkstatt: "", notizen: ""
  });

  const TYPEN = [
    { value: "tuev", label: "TÜV/HU" },
    { value: "au", label: "AU (Abgasuntersuchung)" },
    { value: "inspektion", label: "Inspektion" },
    { value: "oelwechsel", label: "Ölwechsel" },
    { value: "reifen", label: "Reifenwechsel" },
    { value: "bremsen", label: "Bremsen" },
    { value: "reparatur", label: "Reparatur" },
    { value: "sonstiges", label: "Sonstiges" },
  ];

  useEffect(() => {
    if (vehicles.length > 0 && !form.vehicle_id) {
      setForm(f => ({ ...f, vehicle_id: vehicles[0].id }));
    }
  }, [vehicles]);

  const handleSave = async () => {
    if (!form.vehicle_id || !form.beschreibung) {
      toast.error("Bitte Fahrzeug und Beschreibung angeben");
      return;
    }
    try {
      await axios.post(`${API}/reise/vehicles/wartungen?org_id=${getOrgId()}`, {
        ...form,
        kosten: form.kosten ? parseFloat(form.kosten) : 0,
        naechster_termin: form.naechster_termin || null
      });
      toast.success("Wartung erfasst" + (form.naechster_termin ? " – Erinnerung erstellt" : ""));
      setForm({ vehicle_id: vehicles[0]?.id || "", datum: new Date().toISOString().split("T")[0], typ: "inspektion", beschreibung: "", kosten: "", naechster_termin: "", werkstatt: "", notizen: "" });
      setShowForm(false);
      onReload();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Wartungseintrag löschen?")) return;
    try {
      await axios.delete(`${API}/reise/vehicles/wartungen/${id}?org_id=${getOrgId()}`);
      toast.success("Gelöscht");
      onReload();
    } catch { toast.error("Fehler"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--brand-muted)]">{wartungen.length} Wartungseinträge</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> Neue Wartung
        </Button>
      </div>

      {showForm && (
        <Card className="p-4 border-[var(--brand-border)] bg-[var(--brand-bg)]">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Fahrzeug *</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.vehicle_id} onChange={e => setForm({...form, vehicle_id: e.target.value})}>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.kennzeichen} – {v.marke} {v.modell}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Datum *</Label><Input type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} /></div>
            <div>
              <Label className="text-xs">Typ *</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.typ} onChange={e => setForm({...form, typ: e.target.value})}>
                {TYPEN.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs">Beschreibung *</Label><Input placeholder="z.B. Inspektion bei 60.000 km" value={form.beschreibung} onChange={e => setForm({...form, beschreibung: e.target.value})} /></div>
            <div><Label className="text-xs">Kosten ({currency})</Label><Input type="number" step="0.01" placeholder="z.B. 350" value={form.kosten} onChange={e => setForm({...form, kosten: e.target.value})} /></div>
            <div><Label className="text-xs">Werkstatt</Label><Input placeholder="z.B. BMW Autohaus" value={form.werkstatt} onChange={e => setForm({...form, werkstatt: e.target.value})} /></div>
            <div><Label className="text-xs">Nächster Termin</Label><Input type="date" value={form.naechster_termin} onChange={e => setForm({...form, naechster_termin: e.target.value})} /></div>
            <div><Label className="text-xs">Notizen</Label><Input placeholder="Optional" value={form.notizen} onChange={e => setForm({...form, notizen: e.target.value})} /></div>
          </div>
          <p className="text-[10px] text-[var(--brand-muted)] mt-2">Tipp: Wenn du einen nächsten Termin angibst, wird automatisch eine Erinnerung erstellt.</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleSave} className="bg-[var(--brand-ink)] text-white">Speichern</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      {wartungen.length === 0 ? (
        <div className="text-center py-6 text-[var(--brand-muted)]">
          <Wrench className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Noch keine Wartungseinträge.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {wartungen.slice(0, 20).map(w => (
            <div key={w.id} className="flex items-center justify-between rounded-lg border border-[var(--brand-border-light)] px-3 py-2 text-sm">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Wrench className="w-3.5 h-3.5 text-[var(--brand-muted)] shrink-0" />
                <span className="text-xs text-[var(--brand-muted)] w-20 shrink-0">{w.datum}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 shrink-0">{TYPEN.find(t => t.value === w.typ)?.label || w.typ}</span>
                <span className="truncate text-[var(--brand-ink)]">{w.beschreibung}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {w.kosten > 0 && <span className="font-medium text-xs">{fmt(w.kosten)}</span>}
                {w.werkstatt && <span className="text-[10px] text-[var(--brand-muted)]">{w.werkstatt}</span>}
                <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400" onClick={() => handleDelete(w.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========== 5. KOSTENÜBERSICHT ========== */
const KostenUebersicht = ({ kosten, vehicles, statistiken, onReload, getOrgId }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "", datum: new Date().toISOString().split("T")[0],
    kategorie: "versicherung", beschreibung: "", betrag: ""
  });

  const KATEGORIEN = [
    { value: "steuer", label: "Kfz-Steuer" },
    { value: "versicherung", label: "Versicherung" },
    { value: "wartung", label: "Wartung/Inspektion" },
    { value: "reparatur", label: "Reparatur" },
    { value: "leasing", label: "Leasing-Rate" },
    { value: "parkgebuehr", label: "Parkgebühren" },
    { value: "maut", label: "Maut" },
    { value: "waschen", label: "Autowäsche" },
    { value: "sonstiges", label: "Sonstiges" },
  ];

  useEffect(() => {
    if (vehicles.length > 0 && !form.vehicle_id) {
      setForm(f => ({ ...f, vehicle_id: vehicles[0].id }));
    }
  }, [vehicles]);

  const handleSave = async () => {
    if (!form.vehicle_id || !form.beschreibung || !form.betrag) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    try {
      await axios.post(`${API}/reise/vehicles/kosten?org_id=${getOrgId()}`, {
        ...form,
        betrag: parseFloat(form.betrag)
      });
      toast.success("Kosten erfasst");
      setForm({ vehicle_id: vehicles[0]?.id || "", datum: new Date().toISOString().split("T")[0], kategorie: "versicherung", beschreibung: "", betrag: "" });
      setShowForm(false);
      onReload();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Kosteneintrag löschen?")) return;
    try {
      await axios.delete(`${API}/reise/vehicles/kosten/${id}?org_id=${getOrgId()}`);
      toast.success("Gelöscht");
      onReload();
    } catch { toast.error("Fehler"); }
  };

  const totalKosten = kosten.reduce((sum, k) => sum + (k.betrag || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[var(--brand-muted)]">{kosten.length} Kosteneinträge</p>
          {totalKosten > 0 && <p className="text-xs font-medium text-[var(--brand-ink)]">Gesamt: {fmt(totalKosten)}</p>}
        </div>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> Neue Kosten
        </Button>
      </div>

      {showForm && (
        <Card className="p-4 border-[var(--brand-border)] bg-[var(--brand-bg)]">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Fahrzeug *</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.vehicle_id} onChange={e => setForm({...form, vehicle_id: e.target.value})}>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.kennzeichen} – {v.marke} {v.modell}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Datum *</Label><Input type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} /></div>
            <div>
              <Label className="text-xs">Kategorie *</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.kategorie} onChange={e => setForm({...form, kategorie: e.target.value})}>
                {KATEGORIEN.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs">Beschreibung *</Label><Input placeholder="z.B. Haftpflicht 01/2026" value={form.beschreibung} onChange={e => setForm({...form, beschreibung: e.target.value})} /></div>
            <div><Label className="text-xs">Betrag ({currency}) *</Label><Input type="number" step="0.01" placeholder="z.B. 89.50" value={form.betrag} onChange={e => setForm({...form, betrag: e.target.value})} /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleSave} className="bg-[var(--brand-ink)] text-white">Speichern</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      {/* Kosten nach Kategorie Zusammenfassung */}
      {statistiken.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {statistiken.map(s => (
            <div key={s.vehicle.id} className="rounded-lg border border-[var(--brand-border-light)] p-3">
              <div className="text-xs font-medium text-[var(--brand-ink)] mb-1">{s.vehicle.kennzeichen}</div>
              <div className="text-lg font-bold text-[var(--brand-primary)]">{fmt(s.gesamt_kosten)}</div>
              <div className="text-[10px] text-[var(--brand-muted)]">{fmtKm(s.gesamt_km)} km · {fmt(s.kosten_pro_km)}/km</div>
            </div>
          ))}
        </div>
      )}

      {kosten.length === 0 ? (
        <div className="text-center py-6 text-[var(--brand-muted)]">
          <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Noch keine Kosten erfasst.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {kosten.slice(0, 20).map(k => (
            <div key={k.id} className="flex items-center justify-between rounded-lg border border-[var(--brand-border-light)] px-3 py-2 text-sm">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-xs text-[var(--brand-muted)] w-20 shrink-0">{k.datum}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 shrink-0">{KATEGORIEN.find(x => x.value === k.kategorie)?.label || k.kategorie}</span>
                <span className="truncate text-[var(--brand-ink)]">{k.beschreibung}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-medium text-xs">{fmt(k.betrag)}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400" onClick={() => handleDelete(k.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========== 6. ERINNERUNGEN ========== */
const ErinnerungenTab = ({ erinnerungen, vehicles, onReload, getOrgId }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "", typ: "tuev", datum: "", beschreibung: ""
  });

  const TYPEN = [
    { value: "tuev", label: "TÜV/HU" },
    { value: "au", label: "AU" },
    { value: "versicherung", label: "Versicherung" },
    { value: "inspektion", label: "Inspektion" },
    { value: "reifenwechsel", label: "Reifenwechsel" },
    { value: "leasingende", label: "Leasing-Ende" },
    { value: "steuer", label: "Kfz-Steuer" },
    { value: "sonstiges", label: "Sonstiges" },
  ];

  useEffect(() => {
    if (vehicles.length > 0 && !form.vehicle_id) {
      setForm(f => ({ ...f, vehicle_id: vehicles[0].id }));
    }
  }, [vehicles]);

  const handleSave = async () => {
    if (!form.vehicle_id || !form.datum) {
      toast.error("Bitte Fahrzeug und Datum angeben");
      return;
    }
    try {
      await axios.post(`${API}/reise/vehicles/erinnerungen?org_id=${getOrgId()}`, form);
      toast.success("Erinnerung erstellt");
      setForm({ vehicle_id: vehicles[0]?.id || "", typ: "tuev", datum: "", beschreibung: "" });
      setShowForm(false);
      onReload();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const toggleErledigt = async (id) => {
    try {
      await axios.put(`${API}/reise/vehicles/erinnerungen/${id}?org_id=${getOrgId()}`);
      onReload();
    } catch { toast.error("Fehler"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Erinnerung löschen?")) return;
    try {
      await axios.delete(`${API}/reise/vehicles/erinnerungen/${id}?org_id=${getOrgId()}`);
      toast.success("Gelöscht");
      onReload();
    } catch { toast.error("Fehler"); }
  };

  const getVehicleLabel = (vid) => {
    const v = vehicles.find(x => x.id === vid);
    return v ? v.kennzeichen : "–";
  };

  const isOverdue = (datum) => {
    if (!datum) return false;
    return new Date(datum) < new Date();
  };

  const offene = erinnerungen.filter(e => !e.erledigt);
  const erledigte = erinnerungen.filter(e => e.erledigt);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--brand-muted)]">{offene.length} offene Erinnerungen</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> Neue Erinnerung
        </Button>
      </div>

      {showForm && (
        <Card className="p-4 border-[var(--brand-border)] bg-[var(--brand-bg)]">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Fahrzeug *</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.vehicle_id} onChange={e => setForm({...form, vehicle_id: e.target.value})}>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.kennzeichen} – {v.marke} {v.modell}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Typ *</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={form.typ} onChange={e => setForm({...form, typ: e.target.value})}>
                {TYPEN.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Fällig am *</Label><Input type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} /></div>
            <div className="sm:col-span-3"><Label className="text-xs">Beschreibung</Label><Input placeholder="z.B. TÜV fällig im März 2026" value={form.beschreibung} onChange={e => setForm({...form, beschreibung: e.target.value})} /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleSave} className="bg-[var(--brand-ink)] text-white">Speichern</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      {offene.length === 0 && erledigte.length === 0 ? (
        <div className="text-center py-6 text-[var(--brand-muted)]">
          <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Keine Erinnerungen vorhanden.</p>
        </div>
      ) : (
        <>
          {offene.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-[var(--brand-ink)]">Offen ({offene.length})</p>
              {offene.map(e => (
                <div key={e.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${isOverdue(e.datum) ? "border-red-200 bg-red-50/50" : "border-[var(--brand-border-light)]"}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {isOverdue(e.datum) ? <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" /> : <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                    <span className="text-xs text-[var(--brand-muted)] w-20 shrink-0">{e.datum}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 shrink-0">{TYPEN.find(t => t.value === e.typ)?.label || e.typ}</span>
                    <span className="truncate text-[var(--brand-ink)]">{e.beschreibung || "–"}</span>
                    <span className="text-[10px] text-[var(--brand-muted)]">[{getVehicleLabel(e.vehicle_id)}]</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-500" onClick={() => toggleErledigt(e.id)} title="Als erledigt markieren">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400" onClick={() => handleDelete(e.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {erledigte.length > 0 && (
            <div className="space-y-1.5 mt-4">
              <p className="text-xs font-medium text-[var(--brand-muted)]">Erledigt ({erledigte.length})</p>
              {erledigte.slice(0, 5).map(e => (
                <div key={e.id} className="flex items-center justify-between rounded-lg border border-[var(--brand-border-light)] px-3 py-2 text-sm opacity-60">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span className="text-xs text-[var(--brand-muted)] w-20 shrink-0">{e.datum}</span>
                    <span className="truncate text-[var(--brand-ink)] line-through">{e.beschreibung || e.typ}</span>
                  </div>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400 shrink-0" onClick={() => handleDelete(e.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ========== 7. AUSWERTUNGEN ========== */
const Auswertungen = ({ statistiken, vehicles, fahrten }) => {
  if (statistiken.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--brand-muted)]">
        <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="text-sm">Noch keine Daten für Auswertungen vorhanden.</p>
        <p className="text-xs mt-1">Erfasse Fahrten, Tankeinträge und Kosten, um Statistiken zu sehen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--brand-muted)]">Übersicht aller Fahrzeugdaten</p>

      {statistiken.map(s => (
        <Card key={s.vehicle.id} className="p-4 border-[var(--brand-border)]">
          <div className="flex items-center gap-2 mb-3">
            <Car className="w-4 h-4 text-[var(--brand-primary)]" />
            <span className="font-semibold text-sm text-[var(--brand-ink)]">{s.vehicle.kennzeichen} – {s.vehicle.marke} {s.vehicle.modell}</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div className="rounded-lg p-2.5 bg-[var(--brand-bg)]">
              <div className="text-[10px] text-[var(--brand-muted)]">Gesamtkilometer</div>
              <div className="text-base font-bold">{fmtKm(s.gesamt_km)} km</div>
            </div>
            <div className="rounded-lg p-2.5 bg-[var(--brand-bg)]">
              <div className="text-[10px] text-[var(--brand-muted)]">Geschäftlich</div>
              <div className="text-base font-bold text-emerald-600">{s.geschaeftlich_anteil}%</div>
              <div className="text-[10px] text-[var(--brand-muted)]">{fmtKm(s.geschaeftlich_km)} km</div>
            </div>
            <div className="rounded-lg p-2.5 bg-[var(--brand-bg)]">
              <div className="text-[10px] text-[var(--brand-muted)]">Gesamtkosten</div>
              <div className="text-base font-bold">{fmt(s.gesamt_kosten)}</div>
              <div className="text-[10px] text-[var(--brand-muted)]">{fmt(s.kosten_pro_km)}/km</div>
            </div>
            <div className="rounded-lg p-2.5 bg-[var(--brand-bg)]">
              <div className="text-[10px] text-[var(--brand-muted)]">km-Pauschale</div>
              <div className="text-base font-bold text-emerald-600">{fmt(s.kilometerpauschale_geschaeftlich)}</div>
              <div className="text-[10px] text-[var(--brand-muted)]">{fmtKm(s.geschaeftlich_km)} km × 0,30€</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            <div className="flex justify-between border-b border-[var(--brand-border-light)] pb-1">
              <span className="text-[var(--brand-muted)]">Fahrten</span>
              <span className="font-medium">{s.fahrten_anzahl}</span>
            </div>
            <div className="flex justify-between border-b border-[var(--brand-border-light)] pb-1">
              <span className="text-[var(--brand-muted)]">Verbrauch</span>
              <span className="font-medium">{s.verbrauch_pro_100km} L/100km</span>
            </div>
            <div className="flex justify-between border-b border-[var(--brand-border-light)] pb-1">
              <span className="text-[var(--brand-muted)]">Tankkosten</span>
              <span className="font-medium">{fmt(s.gesamt_tankkosten)}</span>
            </div>
            <div className="flex justify-between border-b border-[var(--brand-border-light)] pb-1">
              <span className="text-[var(--brand-muted)]">Offene Erinnerungen</span>
              <span className={`font-medium ${s.offene_erinnerungen > 0 ? "text-red-500" : ""}`}>{s.offene_erinnerungen}</span>
            </div>
            <div className="flex justify-between border-b border-[var(--brand-border-light)] pb-1">
              <span className="text-[var(--brand-muted)]">Privat-km</span>
              <span className="font-medium">{fmtKm(s.privat_km)} km</span>
            </div>
            <div className="flex justify-between border-b border-[var(--brand-border-light)] pb-1">
              <span className="text-[var(--brand-muted)]">Getankt gesamt</span>
              <span className="font-medium">{s.gesamt_liter} L</span>
            </div>
          </div>

          {Object.keys(s.kosten_nach_kategorie || {}).length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-medium text-[var(--brand-muted)] mb-1">Kosten nach Kategorie:</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(s.kosten_nach_kategorie).filter(([,v]) => v > 0).sort((a,b) => b[1] - a[1]).map(([kat, betrag]) => (
                  <span key={kat} className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand-bg)] border border-[var(--brand-border-light)]">
                    {kat}: {fmt(betrag)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      ))}

      <p className="text-[10px] text-center text-[var(--brand-muted)]">
        Alle Daten basieren auf deinen Eingaben. Kilometerpauschale: 0,30 €/km (geschäftliche Fahrten).
      </p>
    </div>
  );
};

export default FahrzeugStudio;
